import { AD_STYLES_18, AD_ANGLES_18, STYLE_ALIASES, ANGLE_ALIASES, type AdStyle, type AdAngle } from './constants/adStyles';

export interface CreativeAllocation {
  count: number;
  style?: AdStyle;
  angle?: AdAngle;
  /** A requested visual format without a canonical adStyle; retain it verbatim in planning. */
  format?: string;
}

const WORD_COUNTS: Record<string, number> = {one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,eleven:11,twelve:12,twenty:20,thirty:30,forty:40,fifty:50,hundred:100};
const OUTPUT_MODIFIERS='(?:(?:more|new|fresh|additional|complete|full|detailed|saved|editable|numbered|original|distinct|different|separate|exactly)\\s+)*';
const OUTPUT_NOUN='(?:ad\\s+(?:briefs?|concepts?)|ads?|briefs?|concepts?|creatives?|images?)(?![a-z])';
const genericOutput=new RegExp('^'+OUTPUT_MODIFIERS+OUTPUT_NOUN,'i');
const beforeAfterPattern=String.raw`before(?:\s*[-/&]\s*|\s+(?:and\s+)?|\s*-\s*and\s*-\s*)after`;
/** A format named after the total still governs the whole batch. Quoted copy,
 * source descriptions, examples and negative instructions do not set a format. */
export function requestedBeforeAfterScene(brief:string):boolean {
 if(formatFirstCreativeRequest(brief)?.style==='Before & After')return true;
 const text=brief.replace(/"(?:\\.|[^"\\])*"|“[^”]*”|‘[^’]*’|(?<![\p{L}\p{N}])'[^'\n]+'(?![\p{L}\p{N}])/gu,'');
 const scoped=/REQUIRED BATCH FORMAT:\s*([^\n]+)/i.exec(text);
 if(scoped)return new RegExp('^'+beforeAfterPattern+'$','i').test(scoped[1].trim());
 for(const match of text.matchAll(new RegExp('\\b'+beforeAfterPattern+'\\b','gi'))){
  const clause=text.slice(0,match.index).split(/[.!?;\n]/).at(-1)??'';
  const command=Array.from(clause.matchAll(/(?:^|,|\b(?:and|but|then|instead))\s*(?:(?:(?:can|could|would|will)\s+you|(?:I|we)\s+(?:want|need|would like)\s+(?:you\s+)?to)\s+)?(?:please\s+)?(?:make|create|generate|produce|write|plan|use|show|depict|feature|include)\b/gi)).at(-1);
  if(!command)continue;
  const direction=clause.slice(command.index).split(/\b(?:but|however|instead)\b/i).at(-1)??'';
  if(/\b(?:no|not|never|without|avoid|exclude|omit|remove|don['’]t|doesn['’]t|shouldn['’]t|cannot)\b/i.test(direction))continue;
  if(/\b(?:example|such as|e\.g|optional|maybe|including|some|one of|two of|three of)\b/i.test(direction))continue;
  const tail=text.slice(match.index!+match[0].length);
  if(/^\s+(?:or|and)\s+(?:UGC|listicle|testimonial|studio|lifestyle|reddit)\b/i.test(tail))continue;
  // "Make 10 ads ... with before/after visuals" and "Use a before/after
  // split" are global visual directions; "including 2 ..." remains a quota.
  const direct=/(?:\b(?:of|with|as|in|using|showing|depicting|featuring|use|show|depict|feature|include)\s+(?:(?:a|an|the|all|only)\s+)*|\b(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten|twenty)\s+)$/.test(direction.toLowerCase());
  if(direct)return true;
 }
 return false;
}
const FORMATS = ['reddit-style', 'reddit posts', 'reddit', 'studio shots', 'studio shot', 'studio photography', 'studio product', 'typographic posters', 'typographic poster', 'text-on-image', 'flat lay', 'comparison chart', 'lifestyle shot', 'split screen'];
const escapeRegex=(text:string)=>text.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
function phrasePattern(phrase:string):string {
 return phrase.split(/[\s\-/&]+/).filter(Boolean).map(escapeRegex).join('[\\s\\-/&]*');
}
const candidates: Array<{pattern:RegExp;value:Omit<CreativeAllocation,'count'>;length:number}> = [];
for(const [alias,style] of Object.entries({...Object.fromEntries(AD_STYLES_18.map(style=>[style.toLowerCase(),style])),...STYLE_ALIASES,'before/after':'Before & After' as AdStyle})) {
 candidates.push({pattern:new RegExp('^'+phrasePattern(alias)+'s?(?![a-z])','i'),value:{style},length:alias.length});
}
for(const [alias,angle] of Object.entries({...Object.fromEntries(AD_ANGLES_18.map(angle=>[angle.toLowerCase(),angle])),...ANGLE_ALIASES})) {
 // "dr" is too ambiguous to treat as an explicit allocation.
 if(alias==='dr')continue;
 candidates.push({pattern:new RegExp('^'+phrasePattern(alias)+'s?(?![a-z])','i'),value:{angle},length:alias.length});
}
for(const format of FORMATS)candidates.push({pattern:new RegExp('^'+phrasePattern(format)+'(?![a-z])','i'),value:{format},length:format.length});
candidates.sort((a,b)=>b.length-a.length);

/** A format-led batch order has the same count/format as “20 before-and-after ads”. */
export function formatFirstCreativeRequest(brief:string):CreativeAllocation|null {
 const text=brief.trim().replace(/^please\s+/i,'');
 const heading=/^([a-z][a-z\s/&-]{1,60}?)\s+(\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|twenty|thirty|forty|fifty)\s+(?:ads?|images?|creatives?)(?=\s*(?:$|[,.:;!])|\s+(?:for|with|without|using|showing|featuring|depicting|in|of|please)\b)/i.exec(text);
 if(!heading)return null;
 const format=heading[1].trim();
 const found=candidates.find(candidate=>candidate.pattern.exec(format)?.[0].length===format.length);
 const count=Number(heading[2])||WORD_COUNTS[heading[2].toLowerCase()];
 return found&&count>0?{count,...found.value}:null;
}

/** Extract only explicit numbered known formats, styles or angles. Unspecified slots remain free.
 * No LLM inference, equal splitting, quota rescaling, or invented canonical styles.
 * Omit totalCount when reading a request whose total is the sum of its named quotas.
 */
export function parseCreativeAllocation(brief:string,totalCount?:number):CreativeAllocation[]|null {
 const formatFirst=formatFirstCreativeRequest(brief);
 const rows:CreativeAllocation[]=formatFirst?[formatFirst]:[];
 const quantities=new RegExp('(?<![\\d.$])(?:\\d{1,6}|\\b'+Object.keys(WORD_COUNTS).join('\\b|\\b')+'\\b)\\s*','gi');
 for(const match of Array.from(brief.matchAll(quantities))) {
  const count=Number(match[0].trim())||WORD_COUNTS[match[0].trim().toLowerCase()];
  if(!Number.isInteger(count)||count<=0)continue;
  const tail=brief.slice(match.index!+match[0].length);
  // "20 complete saved Ad Briefs" is a total, not a visual format named
  // "complete saved Ad". Real named formats below still retain their quotas.
  if(genericOutput.test(tail))continue;
  const found=candidates.find(candidate=>candidate.pattern.test(tail));
  const rawFormat=found ? undefined : /^(?!ads?\b|creatives?\b|images?\b)([a-z][a-z/&-]*(?:\s+[a-z][a-z/&-]*){0,4}\s+(?:ads?|shots?|posters?|splits?|posts?))(?![a-z])/i.exec(tail)?.[1];
  if(!found && (!rawFormat || /\b(?:with|for|in|on|per|each|every|and|of)\b/i.test(rawFormat)))continue;
  const label=found ? found.pattern.exec(tail)![0] : rawFormat!;
  // Numbers of claims/items inside an ad are not numbers of ads.
  if(/^\s*(?:per\b|each\b|in\s+(?:each|every|one|a|the)\b|on\s+(?:each|every|one|a|the)\b)/i.test(tail.slice(label.length)))continue;
  rows.push({count,...(found?.value ?? {format:rawFormat!})});
 }
 if(!rows.length){
  const heading=new RegExp('(?<![\\d.$])('+['\\d{1,6}',...Object.keys(WORD_COUNTS)].join('|')+')\\s*'+OUTPUT_MODIFIERS+OUTPUT_NOUN,'i').exec(brief);
  const count=heading?(Number(heading[1])||WORD_COUNTS[heading[1].toLowerCase()]):totalCount;
  if(!count||!requestedBeforeAfterScene(brief))return null;
  rows.push({count,style:'Before & After'});
 }
 const allocated=rows.reduce((sum,row)=>sum+row.count,0);
 if(totalCount!==undefined && allocated>totalCount)throw new Error(`Requested format quantities (${allocated}) exceed the requested ad count (${totalCount}).`);
 return rows;
}

/** Resolve an explicit overall count first, otherwise sum explicit format clauses.
 * A fallback is only used when the request specifies neither; never cap a requested count.
 */
export function creativeRequestCount(brief:string,fallback=5):number {
 const quantities='(?:\\d{1,6}|'+Object.keys(WORD_COUNTS).join('|')+')';
 const heading=new RegExp('(?<![\\d.$])('+quantities+')\\s*'+OUTPUT_MODIFIERS+OUTPUT_NOUN,'i').exec(brief);
 const explicit=heading ? Number(heading[1])||WORD_COUNTS[heading[1].toLowerCase()] : undefined;
 const rows=parseCreativeAllocation(brief,explicit);
 const count=explicit ?? (rows?.reduce((sum,row)=>sum+row.count,0)) ?? fallback;
 if(!Number.isSafeInteger(count)||count<=0)throw new Error('Requested ad count must be a positive whole number.');
 return count;
}
